// @ts-nocheck
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip, IconButton, Popover, Chip } from '@mui/material';
import { ReactComponent as CheckIcon } from '../../../assets/Icons/lucideIcons/check.svg';
import { ReactComponent as CloseIcon } from '../../../assets/Icons/lucideIcons/x.svg';
import { ReactComponent as AutoAwesomeIcon } from '../../../assets/Icons/lucideIcons/sparkles.svg';
import { useTheme } from '@mui/material/styles';

import { SUGGESTION_STATUS } from '../../../hooks/useAIAutofill';
import BadgePopoverContent from './BadgePopoverContent';

const POPOVER_ANCHOR_ORIGIN = { vertical: 'bottom', horizontal: 'left' };
const POPOVER_TRANSFORM_ORIGIN = { vertical: 'top', horizontal: 'left' };

/**
 * AISuggestionIndicator - Inline indicator for fields with AI suggestions
 * Wraps a form field with visual highlighting and accept/reject mini-buttons
 * when an AI suggestion exists for that attribute.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The form field to wrap
 * @param {Object|null} props.suggestion - The suggestion object from useAIAutofill
 * @param {string|number} props.attributeId - The attribute ID
 * @param {Function} props.onAccept - Handler for accepting the suggestion
 * @param {Function} props.onReject - Handler for rejecting the suggestion
 */
const AISuggestionIndicator = ({
  children,
  suggestion,
  attributeId,
  onAccept,
  onReject,
}) => {
  const theme = useTheme();

  const isPending = suggestion?.status === SUGGESTION_STATUS.PENDING;
  const isAccepted = suggestion?.status === SUGGESTION_STATUS.ACCEPTED;

  const handleAccept = useCallback(
    (event) => {
      event?.stopPropagation();
      if (onAccept) {
        onAccept(attributeId);
      }
    },
    [attributeId, onAccept]
  );

  const handleReject = useCallback(
    (event) => {
      event?.stopPropagation();
      if (onReject) {
        onReject(attributeId);
      }
    },
    [attributeId, onReject]
  );

  const [badgeAnchorEl, setBadgeAnchorEl] = useState(null);
  const closeTimerRef = useRef(null);
  const isBadgePopoverOpen = Boolean(badgeAnchorEl);

  const closeBadgePopover = useCallback(() => {
    setBadgeAnchorEl(null);
  }, [setBadgeAnchorEl]);

  const handleBadgeMouseEnter = useCallback(
    (event) => {
      clearTimeout(closeTimerRef.current);
      setBadgeAnchorEl(event.currentTarget);
    },
    [setBadgeAnchorEl]
  );

  const handleBadgeMouseLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(closeBadgePopover, 200);
  }, [closeBadgePopover]);

  const handlePopoverMouseEnter = useCallback(() => {
    clearTimeout(closeTimerRef.current);
  }, []);

  const popoverSx = useMemo(
    () => ({
      pointerEvents: 'none',
      '& .MuiPopover-paper': {
        pointerEvents: 'auto',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        border: `1px solid ${theme?.palette?.other?.borderColor}`,
        overflow: 'hidden',
        width: 260,
      },
    }),
    [theme?.palette?.other?.borderColor]
  );

  const popoverPaperProps = useMemo(
    () => ({
      onMouseEnter: handlePopoverMouseEnter,
      onMouseLeave: closeBadgePopover,
    }),
    [handlePopoverMouseEnter, closeBadgePopover]
  );

  // No suggestion or already rejected — render children as-is
  if (!suggestion || suggestion?.status === SUGGESTION_STATUS.REJECTED) {
    return children;
  }

  const borderColor = isPending
    ? theme?.palette?.other?.aiPrimary
    : theme?.palette?.success?.main;

  const bgColor = isPending
    ? theme?.palette?.other?.aiHighlightBackgroundMedium
    : theme?.palette?.other?.successBackgroundColor;

  return (
    <Box
      sx={{
        position: 'relative',
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: '4px',
        backgroundColor: bgColor,
        transition: 'all 0.2s ease-in-out',
      }}
    >
      {/* Top bar with AI badge and action buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 0.75,
          pt: 0.5,
          pb: 0,
          minHeight: 22,
        }}
      >
        {/* AI badge */}
        <Chip
          onMouseEnter={handleBadgeMouseEnter}
          onMouseLeave={handleBadgeMouseLeave}
          size="small"
          icon={<AutoAwesomeIcon sx={{ size: 11 }} />}
          label={isPending ? 'AI Suggestion' : 'Accepted'}
          className={`ai-badge-chip ${isPending ? 'pending' : 'accepted'}`}
          sx={{
            cursor: 'default',
            mb: 0.5,
          }}
        />
        <Popover
          open={isBadgePopoverOpen}
          anchorEl={badgeAnchorEl}
          onClose={closeBadgePopover}
          anchorOrigin={POPOVER_ANCHOR_ORIGIN}
          transformOrigin={POPOVER_TRANSFORM_ORIGIN}
          disableRestoreFocus
          sx={popoverSx}
          slotProps={{
            paper: popoverPaperProps,
          }}
        >
          <BadgePopoverContent
            suggestion={suggestion}
            isPending={isPending}
            isAccepted={isAccepted}
          />
        </Popover>

        {/* Inline accept/reject buttons for pending suggestions */}
        {isPending && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Accept suggestion" arrow>
              <IconButton
                size="small"
                onClick={handleAccept}
                aria-label="Accept AI suggestion"
                className="ai-action-accept-sm"
              >
                <CheckIcon sx={{ size: 13 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reject suggestion" arrow>
              <IconButton
                size="small"
                onClick={handleReject}
                aria-label="Reject AI suggestion"
                className="ai-action-reject-sm"
              >
                <CloseIcon sx={{ size: 13 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      {/* Form field */}
      <Box sx={{ px: 0.5, pb: 0.25 }}>{children}</Box>
    </Box>
  );
};

AISuggestionIndicator.propTypes = {
  children: PropTypes.node.isRequired,
  suggestion: PropTypes.shape({
    oldValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
      PropTypes.object,
    ]),
    newValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
      PropTypes.object,
    ]),
    confidence: PropTypes.number,
    status: PropTypes.string,
    attributeLabel: PropTypes.string,
    sectionName: PropTypes.string,
    dataType: PropTypes.string,
  }),
  attributeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onAccept: PropTypes.func,
  onReject: PropTypes.func,
};

export default memo(AISuggestionIndicator);
